import Router from 'next/router';
import { gql } from '@soundxyz/gql-string';
import type { LikeButtonSource } from '@soundxyz/utils/analytics';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ToastContext } from '@context/toast';
import { LikeReleaseDocument, UnlikeReleaseDocument, useMutation } from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { setHideExpandedPlayer } from '@store/audio/AudioMeta';
import { webappUriPath } from '@utils/url';
import { useLikeStatus } from './useLikeStatus';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation LikeRelease($id: UUID!) {
    likeRelease(input: { releaseId: $id }) {
      __typename
      ... on MutationLikeReleaseSuccess {
        data {
          id
          title
        }
      }
      ... on Error {
        message
      }
    }
  }

  mutation UnlikeRelease($id: UUID!) {
    unlikeRelease(input: { releaseId: $id }) {
      __typename
      ... on MutationUnlikeReleaseSuccess {
        data {
          id
          title
        }
      }
      ... on Error {
        message
      }
    }
  }
`);

export const useLikeRelease = ({
  id,
  onUnlikeRelease,
  source,
}: {
  id: string | undefined;
  onUnlikeRelease?: (releaseId: string) => void;
  source: LikeButtonSource;
}) => {
  const { authUser } = useAuth();
  const { login } = useConnectedAuth();
  const { openToast } = ToastContext.useContainer();

  const { refetch, setLiked, isLiked } = useLikeStatus({ id });

  const { mutateAsync: likeRelease } = useMutation(LikeReleaseDocument, {});
  const { mutateAsync: unlikeRelease } = useMutation(UnlikeReleaseDocument, {});

  const onClick = async () => {
    if (!id) return;

    if (authUser?.id) {
      if (!isLiked) {
        // just so it feels snappy
        setLiked(true);
        trackClickEvent({
          event: 'Like Clicked',
          properties: {
            associationId: id,
            associationType: 'Release',
            sourceComponent: source,
          },
        });

        likeRelease(
          { id },
          {
            onSuccess: ({ data }) => {
              refetch();
              if (data?.likeRelease.__typename !== 'MutationLikeReleaseSuccess') {
                // rollback the state
                setLiked(false);
                openToast({
                  text: data?.likeRelease.message || 'Something went wrong while liking the song.',
                  variant: 'error',
                });
                return;
              }
              openToast({
                text: 'You liked this Sound.',
                ctaText: 'View your Liked Sounds.',
                ctaOnClick: () => {
                  Router.push(
                    webappUriPath(authUser.webappUri, {
                      subpath: '/liked-sounds',
                    }),
                  );

                  setHideExpandedPlayer(true);
                },
                variant: 'success',
              });
            },
          },
        );
      } else {
        // just so it feels snappy
        setLiked(false);
        trackClickEvent({
          event: 'Unlike Clicked',
          properties: {
            associationId: id,
            associationType: 'Release',
            sourceComponent: source,
          },
        });

        unlikeRelease(
          { id },
          {
            onSuccess: ({ data }) => {
              refetch();
              if (data?.unlikeRelease.__typename === 'MutationUnlikeReleaseSuccess') {
                onUnlikeRelease?.(id);
                openToast({
                  text: 'You unliked this Sound.',
                  variant: 'success',
                });
              } else {
                // rollback the state
                setLiked(true);

                openToast({
                  text:
                    data?.unlikeRelease.message ||
                    'Something went wrong while unliking this Sound.',
                  variant: 'error',
                });
              }
            },
          },
        );
      }

      return;
    }

    return login();
  };

  return {
    /** status of release */
    isLiked,
    /** callback handler that will take care of liking/unliking a release */
    onClick,
  };
};
